import { Code, ConnectError } from '@bufbuild/connect';

/**
 * A failed scribe call. `message` is the text scribe sent, because that is what
 * the UI renders; ConnectError.message would prefix it with the status code.
 * It is empty when scribe sent an error type this client does not render, so a
 * caller showing it must fall back to its own message on an empty string.
 */
export class ScribeError extends Error {
  constructor(
    message: string | undefined,
    public readonly code: Code,
    public readonly metadata: Headers,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = 'ScribeError';
  }
}

// The backend's shared error types serialize themselves as JSON, so a scribe
// message can arrive as an object rather than a sentence.
type ServiceError =
  | { type: 'NotFoundError'; entity: string }
  | { type: 'AlreadyExistError'; entity: string }
  | { type: 'PermissionDeniedError'; reason: string }
  | { type: 'RequiredArgumentMissingError'; argument: string }
  | {
      type: 'InvalidArgumentValueError';
      argument: string;
      actualValue: string;
    }
  | { type: 'BadRequestError'; reason: string }
  | { type: 'UnprocessableEntityError'; reason: string };

const readServiceError = (message: string): ServiceError | null => {
  if (!message.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(message) as ServiceError;

    return typeof parsed?.type === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

// ServiceError describes what the backend may send, not what it will: the type
// is asserted from arbitrary JSON, and svc_errors has types this switch does not
// render (InternalServerError carries the raw Go failure, UserBadRequestError a
// code with no mapping here). Those yield undefined, and the caller falls back
// to its own localized message.
const serviceErrorText = (error: ServiceError): string | undefined => {
  switch (error.type) {
    case 'NotFoundError':
      return `${error.entity} not found`;
    case 'AlreadyExistError':
      return `${error.entity} already exists`;
    case 'PermissionDeniedError':
      return error.reason;
    case 'RequiredArgumentMissingError':
      return `${error.argument} is required`;
    case 'InvalidArgumentValueError':
      return error.actualValue
        ? `invalid ${error.argument}: ${error.actualValue}`
        : `invalid ${error.argument}`;
    case 'BadRequestError':
    case 'UnprocessableEntityError':
      return error.reason;
  }
};

const scribeMessage = (rawMessage: string): string | undefined => {
  const serviceError = readServiceError(rawMessage);

  return serviceError ? serviceErrorText(serviceError) : rawMessage;
};

export const callScribe = async <T>(call: Promise<T>): Promise<T> => {
  try {
    return await call;
  } catch (error) {
    if (!(error instanceof ConnectError)) {
      throw error;
    }

    throw new ScribeError(
      scribeMessage(error.rawMessage),
      error.code,
      error.metadata,
      { cause: error },
    );
  }
};
